import { useEffect, useMemo, useRef, useState } from 'react';
import { catalogOf } from '../catalog.js';
import { groupByShelf, shelfLabel } from '../shelves.js';

const FALLBACK_COVER = 'https://placehold.co/240x360/111827/ffffff?text=Book';
const PAGE_DURATION_MS = 4500;
const WHEEL_LOCK_MS = 500;
const WHEEL_THRESHOLD = 24;
const SWIPE_THRESHOLD_PX = 50;

// Line breaks are explicit rather than left to wrapping: the ribbon is a wide
// rotated band clipped to the cover, so only its middle stretch is visible and
// a long single-line label loses characters off both ends. Rendered with
// white-space: pre-line.
// A shelf of the reader's own has no hand-written form and falls through to
// the shared shelfLabel, on one line.
const SHELF_LABELS = {
  'currently-reading': 'Currently\nReading',
  'to-read': 'To Read',
  read: 'Read',
  'did-not-finish': 'Did Not\nFinish',
};

// Mode 2: auto-advancing, full-bleed page-turn through the books on one
// shelf. `seed` (shelfKey + optional bookId) picks which shelf/book to start
// on. Navigating past either end (auto-advance, click, swipe, or wheel) hands
// control back to whichever mode preceded it via onExit.
export default function BookHighlight({ books, settings, active, seed, onExit }) {
  // Grouped with the same settings the shelf used, so paging forward from a
  // book lands on the one drawn next to it rather than on a different order.
  const shelves = useMemo(() => groupByShelf(books, settings), [books, settings]);
  const shelfKey = seed?.shelfKey || shelves[0]?.shelfKey;
  const shelfBooks = useMemo(
    () => shelves.find((shelf) => shelf.shelfKey === shelfKey)?.books || [],
    [shelves, shelfKey]
  );

  const startIndex = useMemo(() => {
    if (!seed?.bookId) return 0;
    const seeded = shelfBooks.findIndex((candidate) => candidate.id === seed.bookId);
    return seeded === -1 ? 0 : seeded;
  }, [seed, shelfBooks]);

  const pausedRef = useRef(false);
  const containerRef = useRef(null);
  const coverFrameRef = useRef(null);
  const coverImgRef = useRef(null);
  const touchStartXRef = useRef(0);
  const wheelLockRef = useRef(false);

  const [index, setIndex] = useState(startIndex);
  const [paused, setPaused] = useState(false);
  const [coverBounds, setCoverBounds] = useState(null);
  const [syncedTo, setSyncedTo] = useState({ seed, active });

  // Re-seed during render rather than from an effect. Effects run after the
  // render commits, so opening a shelf shorter than the outgoing index -
  // leaving To Read at book 20 for the single book on Currently Reading -
  // used to render past the end of the new shelf and throw before the effect
  // could correct it. Adjusting state here makes React discard this render
  // and re-run it with the corrected index, so nothing reaches the DOM.
  if (syncedTo.seed !== seed || syncedTo.active !== active) {
    setSyncedTo({ seed, active });
    setIndex(startIndex);
    setPaused(false);
    pausedRef.current = false;
  }

  // The book list can also change under an unchanged seed - a refresh dropping
  // a book off the shelf - so never let the index address past the end.
  const safeIndex = index < shelfBooks.length ? index : 0;
  const book = shelfBooks[safeIndex];

  // object-fit: contain letterboxes the cover within its box without
  // shrinking the element's own rect, so a ribbon clipped to the actual
  // artwork (not the blurred backdrop around it) needs the rendered cover
  // bounds computed by hand from its natural aspect ratio. Uses
  // offsetWidth/offsetHeight (layout size, transform-agnostic) rather than
  // getBoundingClientRect - the page-turn animation's rotateY transform is
  // still running when the image's onLoad fires, and getBoundingClientRect
  // would report the mid-flip projected rect instead of the resting size.
  function measureCoverBounds() {
    const img = coverImgRef.current;
    const frame = coverFrameRef.current;
    if (!img || !frame || !img.naturalWidth || !img.naturalHeight) return;

    const frameWidth = frame.offsetWidth;
    const frameHeight = frame.offsetHeight;
    const containerRatio = frameWidth / frameHeight;
    const imageRatio = img.naturalWidth / img.naturalHeight;

    let renderWidth;
    let renderHeight;
    if (imageRatio > containerRatio) {
      renderWidth = frameWidth;
      renderHeight = frameWidth / imageRatio;
    } else {
      renderHeight = frameHeight;
      renderWidth = frameHeight * imageRatio;
    }

    setCoverBounds({
      top: `${(frameHeight - renderHeight) / 2}px`,
      left: `${(frameWidth - renderWidth) / 2}px`,
      width: `${renderWidth}px`,
      height: `${renderHeight}px`,
      // The ribbon inside is sized as a fraction of the cover rather than in
      // fixed pixels, so it needs the width as a number it can do arithmetic
      // on. A percentage would not do: the ribbon is rotated 45 degrees and
      // overhangs the clip on purpose, so its offsets have to be worked out
      // against the width even where they are vertical.
      '--cover-w': `${renderWidth}px`,
    });
  }

  function pauseAutoplay() {
    if (!pausedRef.current) {
      pausedRef.current = true;
      setPaused(true);
    }
  }

  function goNext() {
    pauseAutoplay();
    setIndex((current) => {
      if (current + 1 >= shelfBooks.length) {
        onExit();
        return current;
      }
      return current + 1;
    });
  }

  function goPrev() {
    pauseAutoplay();
    setIndex((current) => {
      if (current - 1 < 0) {
        onExit();
        return current;
      }
      return current - 1;
    });
  }

  useEffect(() => {
    if (!active || shelfBooks.length === 0 || paused) return undefined;
    const timer = setTimeout(() => {
      setIndex((current) => {
        if (current + 1 >= shelfBooks.length) {
          onExit();
          return current;
        }
        return current + 1;
      });
    }, PAGE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [active, safeIndex, shelfBooks, paused, onExit]);

  useEffect(() => {
    if (!active) return undefined;
    function handleKeyDown(event) {
      if (event.key === 'Escape') onExit();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, onExit]);

  useEffect(() => {
    measureCoverBounds();
    window.addEventListener('resize', measureCoverBounds);
    return () => window.removeEventListener('resize', measureCoverBounds);
  }, [book]);

  // Wheel/touch listeners attached natively (not via JSX onWheel/onTouchMove)
  // so preventDefault reliably blocks the ancestor Carousel's own swipe
  // handling - React can make JSX-bound versions of these passive by default.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !active) return undefined;

    function handleMouseMove() {
      pauseAutoplay();
    }

    function handleWheel(event) {
      event.preventDefault();
      event.stopPropagation();
      pauseAutoplay();
      if (wheelLockRef.current) return;
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (Math.abs(delta) < WHEEL_THRESHOLD) return;
      wheelLockRef.current = true;
      setTimeout(() => {
        wheelLockRef.current = false;
      }, WHEEL_LOCK_MS);
      if (delta > 0) goNext();
      else goPrev();
    }

    function handleTouchStart(event) {
      touchStartXRef.current = event.touches[0].clientX;
      pauseAutoplay();
    }

    function handleTouchMove(event) {
      event.preventDefault();
      event.stopPropagation();
    }

    function handleTouchEnd(event) {
      const dx = event.changedTouches[0].clientX - touchStartXRef.current;
      if (Math.abs(dx) > SWIPE_THRESHOLD_PX) {
        if (dx < 0) goNext();
        else goPrev();
      }
    }

    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd);

    return () => {
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
    // goNext/goPrev close over shelfBooks/onExit via the surrounding render,
    // but only need reattaching when the shelf itself or activation changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, shelfBooks]);

  function handleClick(event) {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (event.clientX - bounds.left < bounds.width / 2) {
      goPrev();
    } else {
      goNext();
    }
  }

  if (shelfBooks.length === 0) {
    return <div className="highlight-mode highlight-empty">No books on this shelf yet.</div>;
  }

  const { genres, subjects } = catalogOf(book);

  return (
    <div className="highlight-mode" ref={containerRef}>
      <button type="button" className="highlight-close" onClick={onExit} aria-label="Back to shelf">
        ×
      </button>
      <div key={book.id} className="highlight-page" onClick={handleClick}>
        <img className="highlight-backdrop" src={book.coverUrl || FALLBACK_COVER} alt="" aria-hidden="true" />
        <div className="highlight-cover-frame" ref={coverFrameRef}>
          <img
            ref={coverImgRef}
            className="highlight-cover"
            src={book.coverUrl || FALLBACK_COVER}
            alt={book.title}
            onLoad={measureCoverBounds}
          />
        </div>
        {coverBounds && (
          <div className="highlight-ribbon-clip" style={coverBounds}>
            <span className="highlight-ribbon">{SHELF_LABELS[shelfKey] || shelfLabel(shelfKey)}</span>
          </div>
        )}
        <div className="highlight-overlay">
          <h2>{book.title}</h2>
          <p className="author">{book.author}</p>
          {book.rating ? <p className="rating">★ {book.rating.toFixed(2)} avg</p> : null}
          {shelfKey === 'read' && book.userRating > 0 && (
            <p className="user-rating">
              {'★'.repeat(book.userRating)}
              {'☆'.repeat(5 - book.userRating)}
            </p>
          )}
          {/* What the Library of Congress says the book is. Between the facts
              and the description, because they are facts too - and above the
              description rather than after it, which is clamped and would
              take them with it. Nothing at all when the catalogue has
              nothing: a placeholder would be saying something about the
              catalogue, not the book. */}
          {genres.length > 0 && (
            <ul className="genres" aria-label="Genre">
              {genres.map((genre) => <li key={genre}>{genre}</li>)}
            </ul>
          )}
          {subjects.length > 0 && (
            <p className="subjects">
              <span className="subjects-label">About</span> {subjects.join(' · ')}
            </p>
          )}
          <p className="description">{book.description || 'No description available yet.'}</p>
        </div>
      </div>
      <div className="highlight-progress">
        {shelfBooks.map((shelfBook, i) => (
          <span key={shelfBook.id} className={`highlight-dot${i === safeIndex ? ' active' : ''}`} />
        ))}
      </div>
    </div>
  );
}
